import { TaxStrategy } from '@principle-theorem/accounting';
import {
  CollectionGroup,
  HasFeeSchedules,
  IFeeSchedule,
  IOrganisation,
  IPricingRule,
  IScopedServiceCode,
  IServiceCode,
  IServiceCodeFee,
  ITreatmentConfiguration,
  ITreatmentFee,
  PricingRuleType,
  toScopedServiceCode,
} from '@principle-theorem/principle-core/interfaces';
import {
  AtLeast,
  CollectionReference,
  DocumentReference,
  Firestore,
  IReffable,
  WithRef,
  asDocRef,
  doc,
  initFirestoreModel,
  isSameRef,
  multiSwitchMap,
  query$,
  reduce2DArray,
  subCollection,
  undeletedQuery,
} from '@principle-theorem/shared';
import { compact, first, isNil, isNumber, isUndefined, sortBy } from 'lodash';
import { Observable, OperatorFunction } from 'rxjs';
import { map } from 'rxjs/operators';
import { PricingRule } from '../pricing-rules/pricing-rule';
import { PricingRuleItem } from '../pricing-rules/pricing-rule-item';
import {
  DEFAULT_SCHEDULE_ID,
  getUndeletedScheduleFromInheritance,
} from './fee-schedule-collection';

export class FeeSchedule {
  static init(
    overrides: AtLeast<IFeeSchedule, 'serviceCodeType'>
  ): IFeeSchedule {
    return {
      name: 'Fee Schedule',
      serviceCodes: [],
      ...initFirestoreModel(),
      ...overrides,
    };
  }

  static col(parent: HasFeeSchedules): CollectionReference<IFeeSchedule> {
    return subCollection<IFeeSchedule>(
      parent.ref,
      CollectionGroup.FeeSchedules
    );
  }

  static all$(parent: HasFeeSchedules): Observable<WithRef<IFeeSchedule>[]> {
    return query$(undeletedQuery(this.col(parent))).pipe(
      map((feeSchedule) => sortBy(feeSchedule, ['name']))
    );
  }

  static resolveSchedules$(): OperatorFunction<
    HasFeeSchedules[],
    WithRef<IFeeSchedule>[]
  > {
    return (source$) =>
      source$.pipe(
        multiSwitchMap((parent) => FeeSchedule.all$(parent)),
        reduce2DArray()
      );
  }

  static defaultRef(
    reffable: HasFeeSchedules
  ): DocumentReference<IFeeSchedule> {
    return doc(FeeSchedule.col(reffable), DEFAULT_SCHEDULE_ID);
  }

  static organisationDefaultRef(
    reffable: HasFeeSchedules
  ): DocumentReference<IFeeSchedule> {
    const [rootCollection, organisationUid] = reffable.ref.path.split('/');
    const organisationRef = asDocRef<IOrganisation>(
      `${rootCollection}/${organisationUid}`
    );
    return FeeSchedule.defaultRef({ ref: organisationRef });
  }

  static async getPreferredOrDefault(
    reffable: HasFeeSchedules,
    practiceDefaultFeeScheduleRef?: DocumentReference<IFeeSchedule>,
    patientPreferredFeeScheduleRef?: DocumentReference<IFeeSchedule>
  ): Promise<WithRef<IFeeSchedule>> {
    const candidateRefs = compact([
      patientPreferredFeeScheduleRef,
      practiceDefaultFeeScheduleRef,
      this.defaultRef(reffable),
    ]);

    for (const candidateRef of candidateRefs) {
      const schedule = await getUndeletedScheduleFromInheritance(candidateRef);
      if (schedule) {
        return schedule;
      }
    }

    return Firestore.getDoc(this.organisationDefaultRef(reffable));
  }

  static getServiceFeeByCode(
    schedule: IFeeSchedule,
    code: IScopedServiceCode
  ): IPricingRule | undefined {
    const existing: IServiceCodeFee | undefined = this.findServiceFeeByCode(
      schedule,
      code
    );

    if (!existing) {
      return;
    }

    const firstRuleItem = first(existing.pricingRule.ruleItems);
    if (
      existing.pricingRule.type === PricingRuleType.Flat &&
      isNil(firstRuleItem?.price)
    ) {
      return;
    }

    return existing.pricingRule;
  }

  static upsertServiceFeeByCode(
    schedule: IFeeSchedule,
    serviceCode: IScopedServiceCode,
    pricingRule: IPricingRule
  ): void {
    const existing: IServiceCodeFee | undefined = this.findServiceFeeByCode(
      schedule,
      serviceCode
    );
    if (existing) {
      existing.pricingRule = pricingRule;
      return;
    }
    schedule.serviceCodes.push({
      serviceCode,
      pricingRule,
    });
  }

  static upsertTreatmentFee(
    schedule: IFeeSchedule,
    treatment: IReffable<ITreatmentConfiguration>,
    taxStrategy: TaxStrategy.GSTApplicable | TaxStrategy.GSTFree,
    price?: number,
    pricingRule?: IPricingRule
  ): void {
    const resolvedPrice = !isUndefined(price)
      ? price
      : pricingRule
        ? FeeSchedule.getTreatmentFeeDefaultPrice(pricingRule)
        : undefined;

    const existing = schedule.treatmentFees?.find((fee) =>
      isSameRef(fee.treatment, treatment)
    );

    if (existing) {
      if (taxStrategy) {
        existing.taxStrategy = taxStrategy;
      }

      if (!isUndefined(resolvedPrice)) {
        existing.price = resolvedPrice;
      }

      if (pricingRule) {
        existing.pricingRule = pricingRule;
      }
      return;
    }

    if (!schedule.treatmentFees) {
      schedule.treatmentFees = [];
    }
    schedule.treatmentFees.push({
      treatment: treatment.ref,
      price: resolvedPrice ?? 0,
      pricingRule,
      taxStrategy: taxStrategy ?? TaxStrategy.GSTFree,
    });
  }

  static removeServiceFeeByCode(
    schedule: IFeeSchedule,
    code: IScopedServiceCode
  ): void {
    schedule.serviceCodes = schedule.serviceCodes.filter(
      (serviceCode) =>
        serviceCode.serviceCode.code !== code.code ||
        serviceCode.serviceCode.type !== code.type
    );
  }

  static findServiceFeeByCode(
    schedule: IFeeSchedule,
    code: IScopedServiceCode
  ): IServiceCodeFee | undefined {
    return schedule.serviceCodes.find(
      (serviceCode) =>
        serviceCode.serviceCode.code === code.code &&
        serviceCode.serviceCode.type === code.type
    );
  }

  static getTreatmentFee(
    schedule: IFeeSchedule,
    treatment: DocumentReference<ITreatmentConfiguration>
  ): ITreatmentFee | undefined {
    return schedule.treatmentFees?.find((fee) =>
      isSameRef(fee.treatment, treatment)
    );
  }

  static getTreatmentFeePrice(
    schedule: IFeeSchedule,
    treatment: DocumentReference<ITreatmentConfiguration>
  ): number {
    return FeeSchedule.getTreatmentFee(schedule, treatment)?.price ?? 0;
  }

  static getTreatmentTaxStrategy(
    schedule: IFeeSchedule,
    treatment: DocumentReference<ITreatmentConfiguration>,
    fallbackStrategy = TaxStrategy.GSTFree
  ): TaxStrategy {
    const foundTaxStrategy = schedule.treatmentFees?.find((fee) =>
      isSameRef(fee.treatment, treatment)
    )?.taxStrategy;

    return foundTaxStrategy ?? fallbackStrategy;
  }

  static getTreatmentFeeDefaultPrice(
    pricingRule: IPricingRule
  ): number | undefined {
    if (pricingRule.type === PricingRuleType.Variable) {
      const selectedItem = pricingRule.ruleItems.find((item) => item.selected);
      return selectedItem?.price ?? first(pricingRule.ruleItems)?.price;
    }
    return first(pricingRule.ruleItems)?.price;
  }
}

export function serviceCodeToFee(
  serviceCode: IServiceCode,
  price?: number
): IServiceCodeFee {
  const pricingRule =
    serviceCode.pricingRule ?? buildPricingRule(serviceCode, price);
  return {
    serviceCode: toScopedServiceCode(serviceCode),
    pricingRule,
  };
}

function buildPricingRule(
  serviceCode: IServiceCode,
  priceOverride?: number
): IPricingRule {
  const price = !isUndefined(priceOverride)
    ? priceOverride
    : isNumber(serviceCode.fee)
      ? serviceCode.fee
      : 0;

  return PricingRule.init({
    type: PricingRuleType.Flat,
    ruleItems: [PricingRuleItem.init({ price })],
  });
}
